// src/components/TileBuilder/ChartControls/BarChartSpecificControls.tsx
import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, TextField, Typography, Box } from '@mui/material';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { ChartControlProps, GROUP_MODES } from './ChartControlProps';

const BORDER_COLOR_PRESETS = [
  { value: 'inherit', label: 'Inherit (from bar color)' },
  { value: 'theme', label: 'Theme' },
  { value: '#000000', label: 'Black' },
  { value: '#FFFFFF', label: 'White' },
];

// Derive layout from the chart type (adjust patterns as needed for your app)
const layoutFromChartType = (chartType?: string): 'vertical' | 'horizontal' => {
  const ct = String(chartType ?? '').toLowerCase();

  if (ct.includes('horizontal')) return 'horizontal';
  return 'vertical';
};

const BarChartSpecificControls: React.FC<ChartControlProps> = ({ chartId, settings, dispatch }) => {
  const s: any = settings as any;

  // If chart type is stored elsewhere in your props/settings, point this at the right field.
  // Common patterns: s.chartType, s.chart_type, etc.
  const derivedLayout = layoutFromChartType(s.chartType ?? s.chart_type);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ width: '100%' }}>
        Bar Chart
      </Typography>

      <FormControl sx={{ minWidth: 180 }} size="small">
        <InputLabel>Group Mode</InputLabel>
        <Select
          label="Group Mode"
          value={s.groupMode ?? 'stacked'}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { groupMode: e.target.value as 'stacked' | 'grouped' } as any }))
          }
        >
          {GROUP_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Layout select removed. Layout is derived from chart type. */}
      {/* If you want to surface it read-only, you can display derivedLayout somewhere. */}
      {/* <Typography variant="body2">Layout: {derivedLayout}</Typography> */}

      <TextField
        label="Padding"
        type="number"
        value={s.padding ?? 0.1}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { padding: Number(e.target.value) } as any }))}
        size="small"
        sx={{ width: 160 }}
        inputProps={{ step: 0.01, min: 0, max: 1 }}
        helperText="0..1"
      />

      <TextField
        label="Inner Padding"
        type="number"
        value={s.innerPadding ?? 0}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { innerPadding: Number(e.target.value) } as any }))}
        size="small"
        sx={{ width: 160 }}
        inputProps={{ min: 0 }}
      />

      <TextField
        label="Border Radius"
        type="number"
        value={s.borderRadius ?? 0}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { borderRadius: Number(e.target.value) } as any }))}
        size="small"
        sx={{ width: 160 }}
        inputProps={{ min: 0 }}
      />

      <TextField
        label="Border Width"
        type="number"
        value={s.borderWidth ?? 0}
        onChange={(e) => dispatch(setSettings({ chartId, settings: { borderWidth: Number(e.target.value) } as any }))}
        size="small"
        sx={{ width: 160 }}
        inputProps={{ min: 0 }}
      />

      <FormControl sx={{ minWidth: 220 }} size="small">
        <InputLabel>Border Color</InputLabel>
        <Select
          label="Border Color"
          value={s.borderColor ?? 'inherit'}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { borderColor: e.target.value } as any }))}
        >
          {BORDER_COLOR_PRESETS.map((p) => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
          <MenuItem value="#FF0000">Red</MenuItem>
          <MenuItem value="#00FF00">Green</MenuItem>
          <MenuItem value="#0000FF">Blue</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default BarChartSpecificControls;